import { CustomEventDispatcher } from "@utils/helpers/event-helper"
import { getAllWordsBetween, type WordReference } from "@circleofa/acim-engine"
import { deleteHighlight, newHighlight, type HighlightID } from "@store/highlights"
import type { HighlighterListener } from "./highlights-directive"
import type { WordSelectionBars } from "./wordbox"
import { registerHotKey } from "@utils/underhood/registerHotkey"
import type { HighlighterListenerSettings } from "./highlight-listener-permissions"
import type { ScrollLock } from "./scroll-locker"
import { CopyWord } from "@utils/reading/copy"
import { setImageQuoteWords } from "@utils/image-editor/image-quote"
import { AddEscapeStack, RemoveEscapeStack } from "@utils/underhood/escape-key"
import { NativeSelectionBridge } from "./native-selection-bridge"

/**
 * @file Global Select Handlers is about storing the global state for selection, copying and pasting.
 * Because we are overriding the default text selection, we have to implement a global state, as well
 * as interrupt the normal `ctrl/cmd + c`
 */

/**
 * If set to null, it means we're deselecting from everything.
 * If there is a highlightListener, we're keeping that selection
 * active
 */
type SelectionTarget =
    | {
          preserveOneSelection: false
      }
    | {
          preserveOneSelection: true
          target: HighlighterListener | null
      }

/**
 *
 */
export type GlobalSelectionEmitterEventHandlerMap = {
    deselect: SelectionTarget
    select: { selection: GlobalSelection }
}

export enum SelectionType {
    HIGHLIGHT,
    TEXT,
}

export enum Grabber {
    LEFT,
    RIGHT,
}

export enum TransferHighlightMode {
    KEEP_SELECTION,
    DESELECT,
}

/**
 * If we're moving the grabbers, we want them to be invisible
 */
export enum GrabberState {
    STATIC,
    MOVING,
}

export type GlobalSelection = {
    parentElement: HTMLElement
    /**
     * This is where the highlight works
     */
    contextElement: HTMLElement
    highlighterListener: HighlighterListener
    bars: WordSelectionBars
    start: WordReference
    end: WordReference
    top: number
    grabberState: GrabberState
    permissions: HighlighterListenerSettings
    scrollLock: ScrollLock
} & (
    | {
          type: SelectionType.HIGHLIGHT
          highlightID: HighlightID
      }
    | {
          type: SelectionType.TEXT
      }
)

/**
 * Responsible for receiving and directing a global state for selection
 */
class GlobalSelectHandler extends CustomEventDispatcher<GlobalSelectionEmitterEventHandlerMap> {
    selection: GlobalSelection | null = null
    constructor() {
        super()

        registerHotKey("Backspace", (down) => {
            if (down) {
                if (this.selection !== null && this.selection.type === SelectionType.HIGHLIGHT) {
                    this.deleteHighlight()
                }
            }
        })

        // registerHotKey("Escape", (down) => {
        //     if (down) {
        //         if (this.selection !== null) {
        //             this.cancelAll()
        //         }
        //     }
        // })
        //
        registerHotKey("h", (down) => {
            if (down) {
                if (this.selection !== null && this.selection.type === SelectionType.TEXT) {
                    this.createHighlight()
                }
            }
        })
        registerHotKey("c", (down, e) => {
            if (!down) return
            if (!e?.metaKey && !e?.ctrlKey) return
            if (!this.selection) return
            e.preventDefault()
            this.copy()
        })
    }

    share() {
        console.log("Global select share!")
        if (this.selection === null) throw new Error("No selection - cannot copy")
        const words = getAllWordsBetween(
            this.selection.start.displayWord,
            this.selection.end.displayWord,
        )
        new CopyWord(words).share()
    }

    get words() {
        if (this.selection === null) throw new Error("No selection - cannot copy")
        const words = getAllWordsBetween(
            this.selection.start.displayWord,
            this.selection.end.displayWord,
        )
        return words
    }

    image() {
        setImageQuoteWords(this.words)
    }

    copy(alt = false) {
        if (this.selection === null) throw new Error("No selection - cannot copy")
        new CopyWord(this.words).copy(
            true,
            alt
                ? {
                      paragraphNumbers: false,
                      sentenceNumbers: false,
                      boldParagraph: false,
                  }
                : {},
        )
    }

    #dispatchDeselect(preserveOneSelection = false) {
        let detail: SelectionTarget
        if (preserveOneSelection === true) {
            if (this.selection === null)
                throw new Error(
                    "Attempted to deselect all highlight listeners except one, but the selection was not set.",
                )
            detail = {
                preserveOneSelection: true,
                target: this.selection.highlighterListener,
            }
        } else {
            detail = { preserveOneSelection: false }
        }

        this.dispatchEvent(new CustomEvent("deselect", { detail }))
    }

    async deleteHighlight() {
        if (this.selection?.type === SelectionType.HIGHLIGHT) {
            const selection = this.selection
            if (selection.permissions.createHighlights !== true)
                throw new Error(
                    "Attempted to create a highlight on a listener that doesn't have permission.",
                )
            this.cancelAll()
            await deleteHighlight(selection.highlightID)
        } else {
            throw new Error(
                "Attempted to delete highlight but highlight was not set in GlobalSelectHandler",
            )
        }
    }

    createHighlight(transferMode: TransferHighlightMode = TransferHighlightMode.DESELECT) {
        if (this.selection === null) {
            console.log("No range set!")
            return
        }
        if (this.selection.permissions.createHighlights !== true)
            throw new Error(
                "Attempted to create a highlight on a listener that doesn't have permission.",
            )
        if (this.selection.type === SelectionType.TEXT) {
            const newID = newHighlight(this.selection.start.path, this.selection.end.path)
            if (transferMode === TransferHighlightMode.KEEP_SELECTION) {
                this.selection.highlighterListener.transferTextToHighlight(newID)
            } else {
                this.cancelAll()
            }
            return newID
        }
    }

    /**
     * This should be called every time there is a selection, so that
     * we can propagate to all subscribers that they should deselect
     * their selection
     */
    registerSelection(
        start: WordReference,
        end: WordReference,
        parentElement: HTMLElement,
        contextElement: HTMLElement,
        highlighterListener: HighlighterListener,
        top: number,
        bars: WordSelectionBars,
        grabberState: GrabberState,
        permissions: HighlighterListenerSettings,
        scrollLock: ScrollLock,
    ) {
        // console.log("Setting range: ", start.path, end.path)
        this.selection = {
            type: SelectionType.TEXT,
            start,
            end,
            parentElement,
            contextElement,
            highlighterListener,
            top,
            bars,
            grabberState,
            permissions,
            scrollLock,
        }
        this.dispatchEvent(new CustomEvent("select", { detail: { selection: this.selection } }))
    }

    registerHighlight(
        highlightID: HighlightID,
        start: WordReference,
        end: WordReference,
        parentElement: HTMLElement,
        contextElement: HTMLElement,
        highlighterListener: HighlighterListener,
        top: number,
        bars: WordSelectionBars,
        grabberState: GrabberState,
        permissions: HighlighterListenerSettings,
        scrollLock: ScrollLock,
    ) {
        this.selection = {
            type: SelectionType.HIGHLIGHT,
            start,
            end,
            highlightID,
            parentElement,
            contextElement,
            highlighterListener,
            top,
            bars,
            grabberState,
            permissions,
            scrollLock,
        }
        this.dispatchEvent(new CustomEvent("select", { detail: { selection: this.selection } }))
    }

    get currentHighlight() {
        return (
            (this.selection &&
                this.selection.type === SelectionType.HIGHLIGHT &&
                this.selection.highlightID) ||
            null
        )
    }

    /**
     * Triggers the beginning of a grabber drag
     * @param grabber
     */
    startMove(grabber: Grabber) {
        if (this.selection === null)
            throw new Error("Attempted to start move but global selection was null")
        this.selection.highlighterListener.grabberDragStart(grabber)
    }

    /**
     * Takes x,y relative to the parentElement position
     * Triggers a movement of a grabber drag
     * @param x
     * @param y
     */
    triggerMove(x: number, y: number) {
        if (this.selection === null)
            throw new Error("Attempted to move dragger but global selection was null")
        this.selection.highlighterListener.grabberDragMove(x, y)
    }

    /**
     * Triggers the ending of a grabber drag
     */
    doneMove() {
        if (this.selection === null)
            throw new Error("Attempted to finish dragging but global selection was null")
        this.selection.highlighterListener.grabberDragEnd()
    }

    /**
     * Cancel is called by the {@link HighlighterListener} that has
     */
    cancel(from: HighlighterListener) {
        if (this.selection?.highlighterListener === from) {
            this.selection = null
            this.#dispatchDeselect(false)
        }
    }

    cancelAll() {
        this.selection = null
        this.#dispatchDeselect(false)
    }
}

export const globalSelectHandler = new GlobalSelectHandler()

// Native browser selections (currently enabled for footnotes) participate in
// the same one-active-selection-at-a-time system as the custom reader engine.
if (globalThis.document) {
    new NativeSelectionBridge(globalThis.document, globalSelectHandler, () =>
        globalSelectHandler.cancelAll(),
    )
}

// Handles using the escape key
const escapeKeyFn = { name: "GlobalSelectHandler", fn: () => globalSelectHandler.cancelAll() }
globalSelectHandler.addEventListener("select", () => {
    AddEscapeStack(escapeKeyFn)
})
globalSelectHandler.addEventListener("deselect", () => {
    RemoveEscapeStack(escapeKeyFn)
})
